import React, { useState, type FC } from 'react'
import styled from 'styled-components'
import type {
  EditorBoxLayoutDescription,
  EditorFitExplanation,
  EditorFitState,
  EditorInspectBlockRole,
  EditorInspectLine,
} from '@/utils/editor-text-box/editorTextBoxAdapter'

type BoxInspection = {
  fit: EditorFitExplanation | null
  layout: EditorBoxLayoutDescription | null
}

const STATE_COLOR: Record<EditorFitState, string> = {
  authored: '#3fb950',
  grown: '#58a6ff',
  shrunk: '#d29922',
  overflowed: '#f85149',
}
const STATE_LABEL: Record<EditorFitState, string> = {
  authored: 'Auto-fit — fits',
  grown: 'Scaled up to fill the box',
  shrunk: 'Shrunk to fit',
  overflowed: 'Overflowing',
}

const bindingSentence = (fit: EditorFitExplanation) => {
  switch (fit.bindingAxis) {
    case 'height':
      return 'Height-bound — the box height is the limit. Make the box taller to grow the text.'
    case 'width':
      if (fit.longWord)
        return 'Width-bound by a long word — it can’t wrap. Press ⏎, widen the box, or turn on word-wrap.'
      return fit.state === 'grown'
        ? 'Width-bound — the text grew to fill the width.'
        : 'Width-bound — the line fills the width. Widen the box or press ⏎ for a new line.'
    case 'both':
      return 'Bound on both edges — the box is exactly full.'
    default:
      return 'Room to grow — capped only by the size ceiling.'
  }
}

const round = (n: number) =>
  Number.isFinite(n) ? Math.round(n * 10) / 10 : '—'
const px = (n: number) => (Number.isFinite(n) ? Math.round(n) : '—')

const ROLE_TAG: Record<EditorInspectBlockRole, string> = {
  paragraph: '¶',
  'blank-runless': '↵ blank (Enter)',
  'blank-empty-run': '∅ blank (cleared)',
}

const Verdict: FC<{ fit: EditorFitExplanation }> = ({ fit }) => {
  const color = STATE_COLOR[fit.state] ?? '#8b949e'
  const span = fit.ceilingScale - fit.minScale
  const pos =
    span > 0
      ? Math.max(0, Math.min(100, ((fit.scale - fit.minScale) / span) * 100))
      : 50
  return (
    <Section>
      <Badge style={{ background: color }}>
        {STATE_LABEL[fit.state] ?? fit.state}
      </Badge>
      <BigSize>
        renders ≈ <b>{round(fit.renderedPt)} pt</b>
        <Muted>
          {' '}
          (authored {round(fit.authoredMaxPt)} × fit {fit.scale.toFixed(2)})
        </Muted>
      </BigSize>
      <Why style={{ borderLeftColor: color }}>{bindingSentence(fit)}</Why>
      <RangeWrap>
        <RangeLabel>min {fit.minScale.toFixed(2)}×</RangeLabel>
        <RangeTrack>
          <RangeFill style={{ width: `${pos}%` }} />
          <RangeDot style={{ left: `${pos}%`, background: color }} />
        </RangeTrack>
        <RangeLabel>ceil {fit.ceilingScale.toFixed(2)}×</RangeLabel>
      </RangeWrap>
      <Summary>{fit.summary}</Summary>
    </Section>
  )
}

const LineRow: FC<{ line: EditorInspectLine }> = ({ line }) => {
  const [open, setOpen] = useState(false)
  return (
    <>
      <Line onClick={() => setOpen((v) => !v)} title="Click for run detail">
        <LineMark $wrap={line.isWrap}>
          {line.isWrap ? '↵ wrap' : '· line'}
        </LineMark>
        <LineText>{line.text || ' '}</LineText>
        <LineMeta>
          w{px(line.width)}
          {line.widthDriver ? ' ◄' : ''}
        </LineMeta>
      </Line>
      {open &&
        line.runs.map((r, i) => (
          <RunRow key={i}>
            <RunDot style={{ background: r.color || '#666' }} />
            <RunText>“{r.text}”</RunText>
            <RunMeta>
              {round(r.authoredPt)}pt→{px(r.renderedPx)}px · {r.fontFamily}
              {r.letterSpacingPt !== 0 ? ` · ls ${r.letterSpacingPt}pt` : ''}
            </RunMeta>
          </RunRow>
        ))}
    </>
  )
}

const Structure: FC<{ layout: EditorBoxLayoutDescription }> = ({ layout }) => (
  <Section>
    <SectionTitle>
      Structure — {layout.blocks.length} block(s), {layout.paintedLineCount}{' '}
      line(s)
      {layout.degraded ? ' (large box: per-line detail skipped)' : ''}
    </SectionTitle>
    {layout.blocks.map((b) => (
      <Block key={b.index}>
        <BlockHead $blank={b.blank}>
          <BlockTag>{ROLE_TAG[b.role] ?? b.role}</BlockTag>
          <BlockMeta>
            block {b.index} · h{px(b.height)}
            {b.align && b.align !== 'start' ? ` · ${b.align}` : ''}
          </BlockMeta>
        </BlockHead>
        {b.lines.map((ln) => (
          <LineRow key={ln.index} line={ln} />
        ))}
      </Block>
    ))}
  </Section>
)

type EditorTextBoxSizingInspectorProps = {
  boxId: string
  inspection?: BoxInspection | null
}

const EditorTextBoxSizingInspector: FC<EditorTextBoxSizingInspectorProps> = ({
  boxId,
  inspection,
}) => {
  const [collapsed, setCollapsed] = useState(false)
  const fit = inspection?.fit ?? null
  const layout = inspection?.layout ?? null
  const stateColor = fit ? (STATE_COLOR[fit.state] ?? '#8b949e') : '#8b949e'

  if (collapsed) {
    return (
      <Rail
        data-editor-text-sizing-inspector
        $collapsed
        onMouseDown={(e) => e.preventDefault()}
      >
        <CollapsedStrip
          onClick={() => setCollapsed(false)}
          title="Expand sizing inspector"
        >
          <Dot style={{ background: stateColor }} />
          <Vertical>SIZING</Vertical>
        </CollapsedStrip>
      </Rail>
    )
  }

  return (
    <Rail
      data-editor-text-sizing-inspector
      onMouseDown={(e) => e.preventDefault()}
    >
      <Header>
        <Title>Sizing Inspector</Title>
        <BoxId>{boxId}</BoxId>
        <Collapse onClick={() => setCollapsed(true)} title="Collapse">
          ⟩
        </Collapse>
      </Header>
      {!fit && !layout && <Empty>Waiting for the box to settle…</Empty>}
      {fit ? (
        <Verdict fit={fit} />
      ) : (
        layout && (
          <Section>
            <Muted>No fit has run for this box yet; structure only.</Muted>
          </Section>
        )
      )}
      {layout && <Structure layout={layout} />}
      {layout && (
        <Section>
          <SectionTitle>Frame</SectionTitle>
          <FrameLine>
            box {px(layout.frame.width)}×{px(layout.frame.height)} · content{' '}
            {px(layout.content.width)}×{px(layout.content.height)}
          </FrameLine>
        </Section>
      )}
    </Rail>
  )
}

export default React.memo(EditorTextBoxSizingInspector)

type RailProps = {
  $collapsed?: boolean
}

const Rail = styled.div<RailProps>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: ${(p) => (p.$collapsed ? '34px' : '360px')};
  background: #15161b;
  color: #c9d1d9;
  font-family:
    ui-monospace, SFMono-Regular, 'SF Mono', Menlo, Consolas, monospace;
  font-size: 12px;
  line-height: 1.45;
  z-index: 99990;
  overflow-y: auto;
  border-left: 1px solid #2b2f38;
  box-shadow: -6px 0 24px rgba(0, 0, 0, 0.35);
  user-select: none;
`

const CollapsedStrip = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding-top: 14px;
  cursor: pointer;
  height: 100%;
`
const Vertical = styled.div`
  writing-mode: vertical-rl;
  letter-spacing: 2px;
  color: #8b949e;
  font-size: 11px;
`
const Dot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
`

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  position: sticky;
  top: 0;
  background: #15161b;
  border-bottom: 1px solid #2b2f38;
  z-index: 1;
`
const Title = styled.div`
  font-weight: 700;
  color: #f0f6fc;
`
const BoxId = styled.div`
  color: #6e7681;
  font-size: 10px;
  margin-left: auto;
  max-width: 130px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const Collapse = styled.button`
  background: none;
  border: none;
  color: #8b949e;
  cursor: pointer;
  font-size: 14px;
  padding: 0 2px;
  &:hover {
    color: #f0f6fc;
  }
`

const Section = styled.div`
  padding: 12px;
  border-bottom: 1px solid #21262d;
`
const SectionTitle = styled.div`
  color: #8b949e;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.6px;
  margin-bottom: 8px;
`
const Badge = styled.span`
  display: inline-block;
  color: #0d1117;
  font-weight: 700;
  border-radius: 4px;
  padding: 2px 8px;
  font-size: 11px;
`
const BigSize = styled.div`
  margin-top: 8px;
  font-size: 14px;
  b {
    color: #f0f6fc;
  }
`
const Muted = styled.span`
  color: #6e7681;
  font-size: 11px;
`
const Why = styled.div`
  margin-top: 8px;
  padding-left: 8px;
  border-left: 2px solid #58a6ff;
  color: #adbac7;
`
const Summary = styled.div`
  margin-top: 8px;
  color: #768390;
  font-style: italic;
`

const RangeWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 10px;
`
const RangeLabel = styled.span`
  color: #6e7681;
  font-size: 10px;
  white-space: nowrap;
`
const RangeTrack = styled.div`
  position: relative;
  flex: 1;
  height: 4px;
  background: #21262d;
  border-radius: 2px;
`
const RangeFill = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: #30363d;
  border-radius: 2px;
`
const RangeDot = styled.div`
  position: absolute;
  top: 50%;
  width: 9px;
  height: 9px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
`

const Block = styled.div`
  margin-bottom: 8px;
`
type BlockHeadProps = {
  $blank?: boolean
}

const BlockHead = styled.div<BlockHeadProps>`
  display: flex;
  align-items: baseline;
  gap: 6px;
  color: ${(p) => (p.$blank ? '#6e7681' : '#d2a8ff')};
`
const BlockTag = styled.span`
  font-weight: 700;
`
const BlockMeta = styled.span`
  color: #6e7681;
  font-size: 10px;
`
const Line = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 1px 0 1px 10px;
  cursor: pointer;
  &:hover {
    background: #1c2128;
  }
`
type LineMarkProps = {
  $wrap?: boolean
}

const LineMark = styled.span<LineMarkProps>`
  color: ${(p) => (p.$wrap ? '#d29922' : '#6e7681')};
  font-size: 10px;
  white-space: nowrap;
  flex: 0 0 44px;
`
const LineText = styled.span`
  color: #c9d1d9;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const LineMeta = styled.span`
  color: #6e7681;
  font-size: 10px;
  white-space: nowrap;
`
const RunRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  padding: 1px 0 1px 24px;
`
const RunDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex: 0 0 8px;
`
const RunText = styled.span`
  color: #adbac7;
  max-width: 90px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`
const RunMeta = styled.span`
  color: #6e7681;
  font-size: 10px;
`
const FrameLine = styled.div`
  color: #adbac7;
`
const Empty = styled.div`
  padding: 16px 12px;
  color: #6e7681;
`
